import * as React from 'react';
import { Icon } from '../../display';
import { Calendar } from './Calendar';
import { Calendar as CalendarIcon } from 'lucide-react';
import { InputLabel, InputMessage } from '../shared';
import {
  type DatePickerSize,
  sizeHeightClasses,
  formatDate,
  parseDate,
  handleDatePickerKeyDown,
} from './dateUtils';
import { usePickerDismiss } from './usePickerDismiss';

export type { DatePickerSize } from './dateUtils';

/**
 * DatePicker component props
 */
export interface DatePickerProps {
  /**
   * The selected date value
   */
  value?: Date | null;
  /**
   * Callback when the date changes
   */
  onChange?: (date: Date | null) => void;
  /**
   * Label text displayed above the input
   */
  label?: string;
  /**
   * Placeholder text when no date is selected
   */
  placeholder?: string;
  /**
   * Helper text displayed below the input
   */
  helperText?: string;
  /**
   * Whether the input is in an error state
   */
  error?: boolean;
  /**
   * Error message displayed when error is true
   */
  errorMessage?: string;
  /**
   * Whether the field is required
   */
  required?: boolean;
  /**
   * Whether the field is disabled
   */
  disabled?: boolean;
  /**
   * Size variant of the input
   * @default 'md'
   */
  size?: DatePickerSize;
  /**
   * Minimum selectable date
   */
  minDate?: Date;
  /**
   * Maximum selectable date
   */
  maxDate?: Date;
  /**
   * Array of dates that should be disabled
   */
  disabledDates?: Date[];
  /**
   * Date format string for display
   * Supports: 'MM/dd/yyyy', 'dd/MM/yyyy', 'yyyy-MM-dd'
   * @default 'MM/dd/yyyy'
   */
  format?: string;
  /**
   * Custom ID for the input element
   */
  id?: string;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Name attribute for form submission
   */
  name?: string;
  /**
   * Callback when the input receives focus
   */
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /**
   * Callback when the input loses focus
   */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

/**
 * Base classes for the input element
 */
const inputBaseClasses = [
  'w-full',
  'rounded-[6px]',
  'border',
  'pl-3',
  'pr-10',
  'text-[15px]',
  'leading-[22px]',
  'outline-none',
  'transition-all',
  'duration-150',
  'ease-out',
  'placeholder:text-secondary-400',
].join(' ');

/**
 * Get input classes based on error and disabled state
 */
function getInputStateClasses(error: boolean, disabled: boolean): string {
  if (disabled) {
    return 'bg-secondary-100 text-secondary-400 border-secondary-300 cursor-not-allowed';
  }
  if (error) {
    return 'border-error-700 focus:ring-2 focus:ring-error-700/20';
  }
  return 'border-secondary-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-200';
}

/**
 * Get calendar button classes based on disabled state
 */
function getCalendarButtonClasses(disabled: boolean): string {
  const baseClasses = 'absolute inset-y-0 right-0 flex items-center pr-3 transition-colors';
  if (disabled) {
    return `${baseClasses} cursor-not-allowed text-secondary-400`;
  }
  return `${baseClasses} cursor-pointer text-secondary-500 hover:text-secondary-600`;
}

/**
 * DatePicker component for selecting a single date.
 *
 * Features:
 * - Calendar dropdown for date selection
 * - Keyboard navigation (arrow keys to move, Enter to select, Escape to close)
 * - Click outside to close
 * - Today button for quick selection
 * - Min/max date constraints
 * - Custom date formatting
 * - Accessible with proper ARIA attributes
 *
 * @example
 * // Basic usage
 * <DatePicker label="Start Date" onChange={(date) => console.log(date)} />
 *
 * @example
 * // With constraints
 * <DatePicker
 *   label="Birth Date"
 *   minDate={new Date(1900, 0, 1)}
 *   maxDate={new Date()}
 *   format="dd/MM/yyyy"
 * />
 */
const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      value,
      onChange,
      label,
      placeholder = 'Select date',
      helperText,
      error = false,
      errorMessage,
      required = false,
      disabled = false,
      size = 'md',
      minDate,
      maxDate,
      disabledDates,
      format = 'MM/dd/yyyy',
      id: providedId,
      className = '',
      name,
      onFocus,
      onBlur,
    },
    ref
  ) => {
    // Generate stable IDs for accessibility
    const reactId = React.useId();
    const inputId = providedId || `datepicker-${reactId}`;
    const helperId = `datepicker-helper-${reactId}`;
    const calendarId = `datepicker-calendar-${reactId}`;

    // State
    const [isOpen, setIsOpen] = React.useState(false);
    const [inputValue, setInputValue] = React.useState(() => formatDate(value || null, format));
    const [viewDate, setViewDate] = React.useState(() => value || new Date());

    // Refs
    const containerRef = React.useRef<HTMLDivElement>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Combine refs
    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    // Update input value when controlled value changes
    React.useEffect(() => {
      setInputValue(formatDate(value || null, format));
    }, [value, format]);

    // Update view date when value changes
    React.useEffect(() => {
      if (value) {
        setViewDate(value);
      }
    }, [value]);

    // Click outside to close & Escape to close
    const handleEscapeFocus = React.useCallback(() => {
      inputRef.current?.focus();
    }, []);
    usePickerDismiss(containerRef, isOpen, setIsOpen, handleEscapeFocus);

    // Handlers
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);

      const parsedDate = parseDate(newValue, format);
      if (parsedDate) {
        onChange?.(parsedDate);
        setViewDate(parsedDate);
      } else if (newValue === '') {
        onChange?.(null);
      }
    };

    const handleDateSelect = (date: Date) => {
      onChange?.(date);
      setInputValue(formatDate(date, format));
      setIsOpen(false);
      inputRef.current?.focus();
    };

    const handleTodayClick = () => {
      const today = new Date();
      handleDateSelect(today);
    };

    const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      onFocus?.(e);
    };

    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      // Skip blur when focus moves within the picker (e.g. clicking a calendar cell)
      if (containerRef.current?.contains(e.relatedTarget as Node)) {
        return;
      }

      // Validate and fix input on blur
      if (inputValue) {
        const parsedDate = parseDate(inputValue, format);
        if (parsedDate) {
          setInputValue(formatDate(parsedDate, format));
        } else {
          // Reset to last valid value if parse fails
          setInputValue(formatDate(value || null, format));
        }
      }
      onBlur?.(e);
    };

    const handleCalendarButtonClick = () => {
      /* v8 ignore start */
      if (!disabled) {
        /* v8 ignore stop */
        setIsOpen(!isOpen);
      }
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      handleDatePickerKeyDown(e, isOpen, setIsOpen);
    };

    // Determine which message to display
    const showError = error && errorMessage;
    const messageId = (showError || helperText) ? helperId : undefined;

    // Input classes - computed using helper functions
    const inputClasses = [
      sizeHeightClasses[size],
      inputBaseClasses,
      getInputStateClasses(error, disabled),
      className,
    ].join(' ');

    return (
      <div ref={containerRef} className="relative w-full">
        {/* Label */}
        {label && <InputLabel label={label} htmlFor={inputId} required={required} />}

        {/* Input container */}
        <div className="relative">
          <input
            ref={inputRef}
            id={inputId}
            type="text"
            role="combobox"
            name={name}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            aria-invalid={error}
            aria-required={required}
            aria-describedby={messageId}
            aria-expanded={isOpen}
            aria-haspopup="dialog"
            aria-controls={isOpen ? calendarId : undefined}
            className={inputClasses}
            autoComplete="off"
          />

          {/* Calendar icon button */}
          <button
            type="button"
            onClick={handleCalendarButtonClick}
            disabled={disabled}
            tabIndex={-1}
            className={getCalendarButtonClasses(disabled)}
            aria-label="Open calendar"
          >
            <Icon icon={CalendarIcon} size="sm" />
          </button>
        </div>

        {/* Calendar dropdown */}
        {isOpen && (
          <dialog
            id={calendarId}
            className="absolute z-50 mt-1 border-0 p-0 bg-transparent"
            open
            aria-modal="true"
            aria-label="Choose date"
          >
            <Calendar
              selectedDate={value}
              onDateSelect={handleDateSelect}
              minDate={minDate}
              maxDate={maxDate}
              disabledDates={disabledDates}
              viewDate={viewDate}
              onViewDateChange={setViewDate}
              onTodayClick={handleTodayClick}
            />
          </dialog>
        )}

        {/* Helper text or error message */}
        <InputMessage error={error} errorMessage={errorMessage} helperText={helperText} helperId={helperId} />
      </div>
    );
  }
);

DatePicker.displayName = 'DatePicker';

export { DatePicker };
